import debounce from "debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import TextInput from "../../../../components/inputs/textInput/TextInput";
import Loader from "../../../../components/loader/Loader";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import { fetchDashboardSquads } from "../../../../features/squads/squadsSlice";
import OneSquad from "./oneSquad/OneSquad";
import styles from "./allSquads.module.css";

const AllSquads = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const { list, fetchStatus, currentPage, totalPages, currentQuery } =
    useSelector((state) => state.squads);

  const squadsListRef = useRef(null);
  const debouncedSearchRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = squadsListRef.current;
    if (!container || fetchStatus === "loading") return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      currentPage < totalPages
    ) {
      dispatch(
        fetchDashboardSquads({
          page: currentPage + 1,
          query: currentQuery,
        }),
      );
    }
  }, [dispatch, fetchStatus, currentPage, totalPages, currentQuery]);

  useEffect(() => {
    if (fetchStatus !== "idle") return;
    dispatch(fetchDashboardSquads({ page: 1, query: "" }));
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    const container = squadsListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => {
      dispatch(
        fetchDashboardSquads({
          page: 1,
          query: String(value || "").trim(),
        }),
      );
    }, 450);

    return () => {
      if (debouncedSearchRef.current?.clear) {
        debouncedSearchRef.current.clear();
      }
    };
  }, [dispatch]);

  const handleSearch = useCallback((event) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearchRef.current?.(value);
  }, []);

  const renderedSquads = list.map((squad) => (
    <OneSquad
      key={squad?._id}
      squadId={squad?._id}
      name={squad?.name}
      slug={squad?.slug}
      region={squad?.region}
      privacy={squad?.privacy}
      banned={squad?.banned}
      membersCount={squad?.membersCount}
      maxMembers={squad?.maxMembers}
      members={squad?.members || []}
      squadImagePath={squad?.squadImageId?.path}
      createdAt={squad?.createdAt}
      updatedAt={squad?.updatedAt}
    />
  ));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <TextWrapper textStyle="textTitle">جميع السكوادات</TextWrapper>
      </div>

      <div className={styles.allSquadsWrapper}>
        <DisplayContainer ref={squadsListRef} className={styles.squadsDisplay}>
          <div className={styles.searchInputInnerWrapper}>
            <div className={styles.searchInputShell}>
              <TextInput
                height="50px"
                width="100%"
                borderRadius="12px"
                placeHolder="ابحث عن سكواد بالاسم أو التاغ أو صاحب السكواد"
                bgColor={"grey"}
                value={searchValue}
                onChange={handleSearch}
                bordered
              />
            </div>
          </div>

          {renderedSquads}

          {fetchStatus === "loading" && (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          )}

          {fetchStatus !== "loading" && list.length === 0 && (
            <div className={styles.emptyState}>
              <TextWrapper textStyle="textMid">لا يوجد سكوادات</TextWrapper>
            </div>
          )}
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllSquads;
